/* ==========================================================================
   blog-components.js
   同期時に展開される「空のプレースホルダ + data-* 属性」を、ページ表示時に
   Storefront APIで商品データを取得して描画する。
   - 記事HTMLには data-product-handle / data-attrs のみを持たせ、価格等は焼き込まない
     → 常に最新情報。デザイン変更もこのファイル一元管理で完結。
   - HTML骨組みは静的モック（components-preview.html）と一致させること。
   ========================================================================== */
(function () {
  "use strict";

  /* --- §3 属性語彙 → 表示ラベル（component-spec.md と一致させる） --- */
  var ATTR_LABELS = {
    price:        "価格",
    rating:       "評価",
    rating_count: "評価件数",
    flavour:      "香り",
    textures:     "テクスチャー",
    ingredients:  "配合成分"
  };
  var SPEC_DEFAULT_ATTRS = ["price", "rating", "flavour", "textures", "ingredients"];
  var EMPTY = "—";

  /* --- 値の整形 --- */
  function formatPrice(amount, currency) {
    if (amount == null) return null;
    try {
      return new Intl.NumberFormat("ja-JP", { style: "currency", currency: currency || "JPY", maximumFractionDigits: 0 }).format(Number(amount));
    } catch (e) {
      return "¥" + Math.round(Number(amount)).toLocaleString("ja-JP");
    }
  }
  // reviews.rating は {"scale_min","scale_max","value"} のJSON文字列で入っている
  function parseRating(raw) {
    if (!raw) return null;
    try { var o = JSON.parse(raw); return o && o.value != null ? Number(o.value) : null; }
    catch (e) { return null; }
  }

  /* 属性キー1つ分の表示用文字列を返す（欠損は null） */
  function attrValue(product, key) {
    switch (key) {
      case "price":        return formatPrice(product.price, product.currency);
      case "rating": {
        var r = parseRating(product.rating);
        return r == null ? null : "★ " + r.toFixed(2).replace(/\.?0+$/, "");
      }
      case "rating_count": return product.ratingCount || null;
      case "flavour":      return product.flavour || null;
      case "textures":     return product.textures || null;
      case "ingredients":  return product.ingredients || null;
      default:             return null;
    }
  }

  function cell(value) {
    if (value == null || value === "") {
      return '<span class="bc-empty">' + EMPTY + "</span>";
    }
    return escapeHtml(String(value));
  }
  function escapeHtml(s) {
    return s.replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* --- 描画: product-card --- */
  function renderProductCard(el, product) {
    if (!product) { el.hidden = true; return; }
    var ratingVal = parseRating(product.rating);
    var ratingHtml = ratingVal == null ? "" :
      '<div class="bc-rating"><span class="bc-rating__star">★</span>' +
      ratingVal.toFixed(2).replace(/\.?0+$/, "") +
      (product.ratingCount ? '<span class="bc-rating__count">(' + product.ratingCount + ")</span>" : "") +
      "</div>";
    // availableForSale=false は「在庫切れ」（廃番=取扱終了ではない）。価格は常に表示し、在庫切れ時のみ控えめに注記。
    var priceHtml = '<div class="bc-product-card__price bc-price">' + cell(formatPrice(product.price, product.currency)) + "</div>";
    var stockHtml = product.discontinued ? '<span class="bc-badge-oos">在庫切れ</span>' : "";
    el.innerHTML =
      '<a class="bc-product-card__link" href="/products/' + encodeURIComponent(product.handle) + '">' +
        '<div class="bc-product-card__media">' +
          (product.image ? '<img class="bc-product-card__img" src="' + escapeHtml(product.image) + '" alt="' + escapeHtml(product.title || "") + '" loading="lazy">' : "") +
        "</div>" +
        '<div class="bc-product-card__body">' +
          '<h3 class="bc-product-card__title">' + escapeHtml(product.title || "") + "</h3>" +
          ratingHtml + priceHtml + stockHtml +
        "</div>" +
      "</a>";
  }

  /* --- 描画: comparison-table --- */
  function renderComparison(el, products, attrs) {
    products = products.filter(Boolean);      // 無効handleは列ごと描画しない
    if (products.length < 2) { el.hidden = true; return; }
    var head = '<tr><th class="bc-comparison__corner"></th>';
    products.forEach(function (p) {
      head += '<th class="bc-comparison__product">' +
        '<div class="bc-comparison__product-media">' +
          (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title || "") + '" loading="lazy">' : "") +
        "</div>" +
        '<a class="bc-comparison__product-name" href="/products/' + encodeURIComponent(p.handle) + '">' + escapeHtml(p.title || "") +
        (p.discontinued ? ' <span class="bc-badge-oos">在庫切れ</span>' : "") + "</a></th>";
    });
    head += "</tr>";
    var body = "";
    attrs.forEach(function (key) {
      body += '<tr><th class="bc-comparison__attr">' + escapeHtml(ATTR_LABELS[key] || key) + "</th>";
      products.forEach(function (p) { body += "<td>" + cell(attrValue(p, key)) + "</td>"; });
      body += "</tr>";
    });
    el.innerHTML = '<table class="bc-comparison__table"><thead>' + head + "</thead><tbody>" + body + "</tbody></table>";
  }

  /* --- 描画: spec-table --- */
  function renderSpec(el, product, attrs) {
    if (!product) { el.hidden = true; return; }
    var body = "";
    attrs.forEach(function (key) {
      body += "<tr><th>" + escapeHtml(ATTR_LABELS[key] || key) + "</th><td>" + cell(attrValue(product, key)) + "</td></tr>";
    });
    el.innerHTML = '<table class="bc-spec__table"><tbody>' + body + "</tbody></table>";
  }

  function parseList(v) { return (v || "").split(",").map(function (s) { return s.trim(); }).filter(Boolean); }

  /* ==========================================================================
     商品データ取得（Storefront API）
     - 設定はテーマ側Liquidで注入する（トークンをこのファイルに直書きしない）:
         <script>
           window.BlogComponents = window.BlogComponents || {};
           window.BlogComponents.config = {
             shopDomain: "{{ shop.permanent_domain }}",   // 例 xxxx.myshopify.com
             storefrontToken: "＜公開Storefrontアクセストークン＞",
             apiVersion: "2026-07"
           };
         </script>
     - 公開Storefrontトークンは読み取り専用・レート制限付きで、フロントに露出して問題ない種類。
     - metafieldは定義側で access.storefront = PUBLIC_READ になっている必要がある
       （現状 custom._textures / custom._ingredients は要変更）。
     ========================================================================== */
  var CFG = (window.BlogComponents && window.BlogComponents.config) || {};

  // 取得するフィールド。metafieldはStorefront公開済みのものだけ値が返る（非公開はnull）
  var PRODUCT_FRAGMENT =
    "fragment PF on Product {\n" +
    "  handle title availableForSale\n" +
    "  featuredImage { url altText }\n" +
    "  priceRange { minVariantPrice { amount currencyCode } }\n" +
    '  rating: metafield(namespace: "reviews", key: "rating") { value }\n' +
    '  ratingCount: metafield(namespace: "reviews", key: "rating_count") { value }\n' +
    '  flavour: metafield(namespace: "custom", key: "flavour") { value }\n' +
    '  textures: metafield(namespace: "custom", key: "_textures") { value }\n' +
    '  ingredients: metafield(namespace: "custom", key: "_ingredients") { value }\n' +
    "}";

  function mv(node) { return node && node.value != null ? node.value : null; }

  // Storefront APIの生レスポンスを描画側が使う形へ正規化
  function normalize(p) {
    if (!p) return null;                       // handle無効・非公開 → 描画しない
    var mp = (p.priceRange && p.priceRange.minVariantPrice) || {};
    return {
      handle: p.handle,
      title: p.title,
      image: p.featuredImage ? p.featuredImage.url : null,
      price: mp.amount != null ? mp.amount : null,
      currency: mp.currencyCode || "JPY",
      rating: mv(p.rating),                    // JSON文字列のまま（parseRatingで解釈）
      ratingCount: mv(p.ratingCount),
      flavour: mv(p.flavour),
      textures: mv(p.textures),
      ingredients: mv(p.ingredients),
      discontinued: p.availableForSale === false   // TODO: 在庫切れと廃番の区別は要検討
    };
  }

  async function fetchProductsViaStorefront(handles) {
    if (!handles.length) return [];
    if (!CFG.storefrontToken || !CFG.shopDomain) {
      console.warn("[blog-components] Storefront設定が未注入です (config.storefrontToken / shopDomain)。");
      return handles.map(function () { return null; });
    }
    var version = CFG.apiVersion || "2026-07";
    // handleごとにエイリアス化して1リクエストにまとめる。handleは変数で渡す（日本語handleのエンコード事故防止）
    var varDefs = handles.map(function (_, i) { return "$h" + i + ": String!"; }).join(", ");
    var aliases = handles.map(function (_, i) { return "  p" + i + ": product(handle: $h" + i + ") { ...PF }"; }).join("\n");
    var query = "query Products(" + varDefs + ") {\n" + aliases + "\n}\n" + PRODUCT_FRAGMENT;
    var variables = {};
    handles.forEach(function (h, i) { variables["h" + i] = h; });

    try {
      var res = await fetch("https://" + CFG.shopDomain + "/api/" + version + "/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": CFG.storefrontToken
        },
        body: JSON.stringify({ query: query, variables: variables })
      });
      if (!res.ok) { console.error("[blog-components] Storefront API HTTP", res.status); return handles.map(function () { return null; }); }
      var json = await res.json();
      if (json.errors) console.warn("[blog-components] Storefront GraphQL errors", json.errors);
      var data = json.data || {};
      return handles.map(function (_, i) { return normalize(data["p" + i]); });
    } catch (e) {
      console.error("[blog-components] Storefront fetch failed", e);
      return handles.map(function () { return null; });
    }
  }

  async function fetchProducts(handles) {
    if (window.BlogComponents && typeof window.BlogComponents.fetchProducts === "function") {
      return window.BlogComponents.fetchProducts(handles);   // 任意の差し替えフック（テスト用モック等）
    }
    return fetchProductsViaStorefront(handles);
  }

  /* --- 起動 --- */
  async function init() {
    var nodes = Array.prototype.slice.call(document.querySelectorAll("[data-bc-component]"));
    if (!nodes.length) return;

    // 全コンポーネントのhandleをまとめて一括取得（重複排除）
    var wanted = {};
    nodes.forEach(function (el) {
      parseList(el.getAttribute("data-product-handle") || el.getAttribute("data-product-handles"))
        .forEach(function (h) { wanted[h] = true; });
    });
    var handles = Object.keys(wanted);
    var fetched = await fetchProducts(handles);
    var byHandle = {};
    handles.forEach(function (h, i) { byHandle[h] = fetched[i] || null; });

    nodes.forEach(function (el) {
      var type = el.getAttribute("data-bc-component");
      if (type === "product-card") {
        renderProductCard(el, byHandle[el.getAttribute("data-product-handle")]);
      } else if (type === "comparison-table") {
        var hs = parseList(el.getAttribute("data-product-handles"));
        var attrs = parseList(el.getAttribute("data-attrs"));
        renderComparison(el, hs.map(function (h) { return byHandle[h]; }), attrs);
      } else if (type === "spec-table") {
        var attrsS = parseList(el.getAttribute("data-attrs"));
        renderSpec(el, byHandle[el.getAttribute("data-product-handle")], attrsS.length ? attrsS : SPEC_DEFAULT_ATTRS);
      }
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
